import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ThumbnailGalleryModal, ThumbnailDeleteModal, ThumbnailSetMainModal } from './ThumbnailModals';
import { PublishWordPressConfirmationModal, ArchiveConfirmationModal } from '../../ui/atoms';
import checkContentQuality from '../../shared/quality/articleQualityCheck';
import Editor from './Editor';
import generateArticle from './generateArticle';
import { updateArticleStatusToOngoing } from './saveArticle';
import ArticleDetailsTabs from './Tabs/ArticleDetailsTabs';
import FAQTab from './Tabs/FAQTab';
import MetaDataTab from './Tabs/MetaDataTab';

import TopBar from '../../layouts/TopBar/topBar';
import { SourceDrawer, ReferenceDrawer } from '../../features/articles';
import { useReferenceHandler } from '../../features/articles';
// Pexels integration moved to backend worker
import { directusAPI } from '../../api';
import { getArticleDisplayBlocks, parseArticleSourceRaw } from '../../features/articles/lib/articleContent';
import {
  extractRelationDatasets,
  formatQualityCheck,
  isLinkedDatasetIdList,
  parseFaqItems,
  resolveThumbnailState,
} from '../../features/articles/lib/articlePayload';
import { parseGeneratedContent } from '../../features/articles/lib/markdownToEditorJS';
import { parseLinkedSources } from '../../features/articles/lib/sources';
import { downloadArticleDocx } from '../../features/articles/lib/docxExport';
import { formatTimeAgo } from '../../features/articles/lib/formatTimeAgo';
import {
  fetchComplexityFromDirectus,
  fetchContentTypeFromDirectus,
  fetchDatasetsByIds,
  fetchKeywordsFromDirectus,
  fetchLanguageFromDirectus,
  fetchSourcesFromDirectus,
  fetchToneOfVoiceFromDirectus,
  patchArticle,
} from '../../features/articles/services/articleRecordApi';
import { useArticleThumbnailActions, useArticleThumbnailState } from '../../features/articles/hooks/useArticleThumbnails';
import { useArticleLifecycleActions } from '../../features/articles/hooks/useArticleLifecycleActions';
import { useArticleEditorCommands } from '../../features/articles/hooks/useArticleEditorCommands';
import { useSourceReferenceTooltip } from '../../features/articles/hooks/useSourceReferenceTooltip';
import { useSectionScrollSpy } from '../../features/articles/hooks/useSectionScrollSpy';
import ArticleEditorToolbar from '../../features/articles/components/article-editor/ArticleEditorToolbar';
import ArticleSectionTabs from '../../features/articles/components/article-editor/ArticleSectionTabs';
import ArticleThumbnailPanel from '../../features/articles/components/article-editor/ArticleThumbnailPanel';
import ArticleBibliography from '../../features/articles/components/article-editor/ArticleBibliography';
import DeleteArticleModal from '../../features/articles/components/article-editor/DeleteArticleModal';

// Composition root of the article editor. Parsing/export logic lives in
// features/articles/lib, Directus calls in features/articles/services, stateful
// behaviors in features/articles/hooks and page sections in
// features/articles/components/article-editor.
const ArticleDraft = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [saveError, setSaveError] = useState('');
  const [lastSaved, setLastSaved] = useState('');
  const [qualityReport, setQualityReport] = useState({});
  const [isCheckingQuality, setIsCheckingQuality] = useState(false);
  const [status, setStatus] = useState('draft');
  const [articleContent, setArticleContent] = useState(null);
  const editorRef = useRef(null);
  const token = localStorage.getItem('directus_token');
  const autosaveTimeoutRef = useRef(null);

  const tooltipRef = useRef(null);
  const workspaceId = localStorage.getItem('workspace_id');

  const [faqItems, setFaqItems] = useState([]);
  const [metaTitle, setMetaTitle] = useState('');
  const [metaDescription, setMetaDescription] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [selectedBlockType, setSelectedBlockType] = useState('paragraph');
  const [linkedSources, setLinkedSources] = useState([]);
  const [articleSourceRaw, setArticleSourceRaw] = useState(null);
  const [, setSources] = useState([]);
  const [contentType, setContentType] = useState('');
  const [linkedDatasets, setLinkedDatasets] = useState([]);

  // Thumbnail state (+ gallery / delete / set-main modals)
  const thumbnails = useArticleThumbnailState();
  const {
    thumbnailData,
    thumbnailArray,
    currentThumbnailIndex,
    setCurrentThumbnailIndex,
    isDraggingThumbnail,
    isUploadingThumbnail,
    isThumbnailGenerating,
    thumbnailGenerationStartTime,
    showGalleryModal,
    setShowGalleryModal,
    showDeleteModal,
    setShowDeleteModal,
    showSetMainModal,
    setShowSetMainModal,
    thumbnailToDelete,
    setThumbnailToDelete,
    thumbnailToSetMain,
    setThumbnailToSetMain,
    isProcessingThumbnail,
    applyThumbnailState,
  } = thumbnails;

  // Publish / archive / delete (with confirmation modals)
  const {
    showDeleteArticleModal,
    setShowDeleteArticleModal,
    isDeletingArticle,
    showPublishWordPressModal,
    setShowPublishWordPressModal,
    isPublishing,
    showArchiveModal,
    setShowArchiveModal,
    isArchiving,
    handlePublishWebsite,
    confirmPublishWebsite,
    handleDeleteArticle,
    handleArchiveArticle,
    confirmArchiveArticle,
    confirmDeleteArticle,
  } = useArticleLifecycleActions({ id, token, navigate });

  const hasStructuredCitationData = Boolean(
    (Array.isArray(articleSourceRaw?.used_sources) && articleSourceRaw.used_sources.length > 0)
    || (Array.isArray(articleSourceRaw?.available_sources) && articleSourceRaw.available_sources.length > 0)
    || Number(articleSourceRaw?.file_search_results_count || 0) > 0
    || Number(articleSourceRaw?.openai_annotations_raw_count || 0) > 0
  );
  const hasStructuredSourceEnvelope = Number(articleSourceRaw?.schema_version || 0) >= 2;

  // Reference handling
  const {
    extractedReferences,
    selectedReference,
    drawerOpen: referenceDrawerOpen,
    handleReferenceClick,
    closeDrawer: closeReferenceDrawer,
    getUniqueFilenames,
    totalReferences,
    uniqueFiles
  } = useReferenceHandler(articleContent, workspaceId, {
    // For schema v2 we trust article_source_raw envelope and avoid fake "Source 1" regex artifacts.
    enableRegexFallback: !hasStructuredCitationData && !hasStructuredSourceEnvelope
  });

  const fetchArticle = useCallback(async () => {
    try {

      const response = await directusAPI.get(`/items/articles/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          fields: '*,articles_linked_source.articles_sources_id.*'
        }
      });


      const data = response.data.data;

      setTitle(data.title);
      setSlug(data.slug);
      setLastSaved(data.date_updated);
      setStatus(data.status);
      setContentType(data.content_type || '');

      // Extract thumbnail from article_thumbnail_url field (now supports JSON array)
      applyThumbnailState(resolveThumbnailState(data, { thumbnailGenerationStartTime }));

      if (data.article_quality_check) {
        setQualityReport(formatQualityCheck(data.article_quality_check));
      }

      const parsedSourceRaw = parseArticleSourceRaw(data.article_source_raw);
      setArticleSourceRaw(parsedSourceRaw);

      // Drafts show article_draft; finished articles article_body without the references section
      setArticleContent({ blocks: getArticleDisplayBlocks(data) });

      // Parse FAQ data if it exists
      setFaqItems(parseFaqItems(data));
      setMetaTitle(data.seo_title || '');
      setMetaDescription(data.seo_description || '');
      setSources(data.sources || []);

      // Parse articles_linked_source - handle M2M expansion
      setLinkedSources(parseLinkedSources(data));

      // Parse linked_datasets - handle both many-to-many relation and simple ID array
      console.log('Raw linked_datasets from API:', data.linked_datasets);

      if (data.linked_datasets && data.linked_datasets.length > 0) {
        // Check if it's a simple array of IDs [1, 2, 3] or many-to-many relations
        if (isLinkedDatasetIdList(data.linked_datasets)) {
          // Simple array of IDs - fetch datasets manually
          console.log('Simple ID array detected, fetching datasets manually');
          try {
            const datasetsData = await fetchDatasetsByIds(data.linked_datasets);
            console.log('Fetched datasets manually:', datasetsData);
            setLinkedDatasets(datasetsData);
          } catch (error) {
            console.error('Error fetching datasets manually:', error);
            setLinkedDatasets([]);
          }
        } else {
          // Many-to-many relation format
          console.log('Many-to-many relation detected');
          const linkedDatasetsData = extractRelationDatasets(data.linked_datasets);
          console.log('linkedDatasetsData after processing:', linkedDatasetsData);
          setLinkedDatasets(linkedDatasetsData);
        }
      } else {
        setLinkedDatasets([]);
      }

      setLoading(false);
    } catch (error) {
      setSaveError('Error fetching article data: ' + error.message);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- thumbnailGenerationStartTime read-only guard; omit to avoid fetch loop
  }, [id, token]);

  useEffect(() => {
    fetchArticle();
  }, [fetchArticle]);

  // Poll for thumbnail generation
  useEffect(() => {
    if (!isThumbnailGenerating || !id) return;

    console.log('🔄 Starting thumbnail polling...');
    const pollInterval = setInterval(() => {
      console.log('🔄 Checking for thumbnail...');
      fetchArticle();
    }, 10000); // Check every 10 seconds

    // Cleanup interval on unmount or when thumbnail appears
    return () => {
      console.log('🔄 Stopping thumbnail polling');
      clearInterval(pollInterval);
    };
  }, [isThumbnailGenerating, id, fetchArticle]);

  // Auto-trigger quality analysis for completed articles without quality reports
  useEffect(() => {
    const autoAnalyzeQuality = async () => {
      // Wait a bit to ensure article is fully loaded and editor is initialized
      if (!loading &&
          (status === 'completed' || status === 'success') &&
          (!qualityReport || Object.keys(qualityReport).length === 0) &&
          editorRef.current &&
          !isCheckingQuality) {
        setTimeout(() => {
          handleCheckContentQuality();
        }, 1000); // Small delay to ensure everything is ready
      }
    };

    autoAnalyzeQuality();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once when article loads; handleCheckContentQuality is stable enough
  }, [loading, status, qualityReport, isCheckingQuality]);

  // Add effect to check for and add images to articles that don't have them yet
  // Pexels image generation moved to backend worker

  const goBack = () => {
    navigate('/articles');
  };

  const saveArticleToDirectus = useCallback(
    (articleData) => patchArticle(id, token, articleData),
    [id, token]
  );

  const handleGenerateArticle = async () => {
    if (editorRef.current) {
      setLoading(true);
      try {
        const outputData = await editorRef.current.save();

        await updateArticleStatusToOngoing(id);

        const generatedContent = await generateArticle(
          id,
          outputData,
          await fetchKeywordsFromDirectus(id, token),
          await fetchToneOfVoiceFromDirectus(id, token),
          await fetchComplexityFromDirectus(id, token),
          await fetchLanguageFromDirectus(id, token),
          await fetchContentTypeFromDirectus(id, token),
          false, // includeImages
          await fetchSourcesFromDirectus(id, token)
        );

        const articleData = {
          id: id,
          title: outputData.blocks[0]?.data?.text || 'Untitled',
          article_body: generatedContent, // This will be stringified in saveArticleToDirectus
          status: 'published',
          keywords: await fetchKeywordsFromDirectus(id, token),
          tone_of_voice: await fetchToneOfVoiceFromDirectus(id, token),
          fog_index: await fetchComplexityFromDirectus(id, token),
          language: await fetchLanguageFromDirectus(id, token),
          content_type: await fetchContentTypeFromDirectus(id, token),
        };

        await saveArticleToDirectus(articleData, true);

        setLastSaved(new Date().toISOString());
        setStatus('published');
        setArticleContent(parseGeneratedContent(generatedContent));
        setSaveError('');

        // Pexels thumbnail generation handled by backend worker

        await fetchArticle();
      } catch (error) {
        console.error('Error generating article:', error);
        setSaveError(`Error generating article: ${error.message}. Please check the console for more details.`);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSave = useCallback(async () => {
    if (editorRef.current) {
      try {
        setIsSaving(true);

        const outputData = await editorRef.current.save();

        const titleBlock = outputData.blocks.find(block => block.type === 'header' && block.data.level === 1);
        const newTitle = titleBlock ? titleBlock.data.text : title;


        const saveToField = (status === 'published' || status === 'completed' || status === 'success') ? 'article_body' : 'article_draft';

        const articleData = {
          title: newTitle,
          slug,
          [saveToField]: JSON.stringify(outputData),
          article_quality_check: qualityReport,
        };

        await saveArticleToDirectus(articleData);

        setTitle(newTitle);
        setLastSaved(new Date().toISOString());
        setSaveError('');

      } catch (error) {
        setSaveError('Error saving article: ' + error.message);
      } finally {
        setIsSaving(false);
      }
    }
  }, [title, slug, status, qualityReport, saveArticleToDirectus]);

  const handleEditorChange = useCallback(() => {
    if (autosaveTimeoutRef.current) {
      clearTimeout(autosaveTimeoutRef.current);
    }
    autosaveTimeoutRef.current = setTimeout(handleSave, 2000);
  }, [handleSave]);

  const {
    runEditorCommand,
    convertCurrentBlock,
    setCurrentBlockStyle,
  } = useArticleEditorCommands({ editorRef, handleEditorChange, setSelectedBlockType });

  const handleCheckContentQuality = async () => {
    if (editorRef.current) {
      setIsCheckingQuality(true);
      try {
        const outputData = await editorRef.current.save();
        // Fetch keywords from Directus or use a state variable if you're already storing them
        const keywords = await fetchKeywordsFromDirectus(id, token); // Implement this function to fetch keywords
        const result = await checkContentQuality({ title, slug, article_body: outputData }, keywords);
        const formattedResult = Array.isArray(result) ? result : [{ quality_control_report: result }];
        setQualityReport(formattedResult);

        const articleData = {
          article_body: JSON.stringify(outputData),
          article_quality_check: formattedResult,
        };
        await saveArticleToDirectus(articleData);
        setLastSaved(new Date().toISOString());
        setSaveError('');
      } catch (error) {
        setSaveError('Error during quality check: ' + error.message);
      } finally {
        setIsCheckingQuality(false);
      }
    }
  };

  const formatDateUpdated = (date) => formatTimeAgo(t, date);

  // Source marker tooltip + source drawer (document-level listeners)
  const {
    tooltipContent,
    tooltipPosition,
    drawerOpen,
    setDrawerOpen,
    selectedSource,
  } = useSourceReferenceTooltip();

  const {
    handleThumbnailDrop,
    handleThumbnailDragOver,
    handleThumbnailDragLeave,
    handleThumbnailFileSelect,
    handleThumbnailSelect,
    handleThumbnailPrevious,
    handleThumbnailNext,
    handleSetAsThumbnail,
    confirmSetAsThumbnail,
    handleThumbnailRegenerate,
    handleThumbnailDelete,
    confirmDeleteThumbnail,
    handleThumbnailDownload,
  } = useArticleThumbnailActions({ id, token, fetchArticle, thumbnails });

  const generateFAQ = async () => {
    // This would be implemented to generate FAQs from the article content
    console.log('FAQ generation to be implemented');
    // Placeholder implementation:
    const newFaq = [
      { question: 'Sample Question 1?', answer: 'Sample Answer 1.' },
      { question: 'Sample Question 2?', answer: 'Sample Answer 2.' }
    ];
    setFaqItems(newFaq);
    handleSave({ faq: newFaq });
  };

  // Function to download article as DOCX
  const handleDownloadDoc = async () => {
    try {
      if (!editorRef.current) {
        console.error('Editor not available');
        return;
      }

      const outputData = await editorRef.current.save();
      await downloadArticleDocx({ outputData, thumbnailData, title, metaTitle, metaDescription, faqItems });
    } catch (error) {
      console.error('❌ Error generating DOCX:', error);
      window.alert('Failed to generate document. Please try again.');
    }
  };

  // Manual save function for TopBar
  const handleManualSave = () => {
    handleSave();
  };

  const citedKnowledgeBaseSources = articleSourceRaw?.used_sources || [];
  const availableKnowledgeBaseSources = articleSourceRaw?.available_sources || [];
  const externalToolEvidence = Array.isArray(articleSourceRaw?.external_tool_evidence)
    ? articleSourceRaw.external_tool_evidence
    : [];
  const hasBibliography = totalReferences > 0
    || linkedSources.length > 0
    || citedKnowledgeBaseSources.length > 0
    || availableKnowledgeBaseSources.length > 0;

  // Scroll spy to update active tab based on scroll position
  const { activeMainTab, scrollToSection } = useSectionScrollSpy(hasBibliography);

  // Debug logging for UI render
  console.log('Rendering Article - linkedDatasets:', linkedDatasets);
  console.log('linkedDatasets length:', linkedDatasets?.length);
  console.log('faqItems length:', faqItems?.length);

  return (
    <div className="flex flex-col min-h-screen bg-white">
      <TopBar
        onGoBack={goBack}
        loading={loading}
        lastSaved={lastSaved}
        formatDateUpdated={formatDateUpdated}
        mode="article"
        onManualSave={handleManualSave}
        isSaving={isSaving}
        onDownloadDoc={handleDownloadDoc}
        onPublishWebsite={handlePublishWebsite}
        onDeleteArticle={handleDeleteArticle}
        onArchiveArticle={handleArchiveArticle}
      />

      {/* Main Navigation Tabs */}
      <div className="border-b border-gray-200 bg-white sticky top-0 z-10 sticky">
        <div className="flex gap-40">
          <div className="flex-1 lg:pl-4">
            <ArticleSectionTabs
              activeMainTab={activeMainTab}
              scrollToSection={scrollToSection}
              faqItems={faqItems}
              hasBibliography={hasBibliography}
              citedKnowledgeBaseSources={citedKnowledgeBaseSources}
              totalReferences={totalReferences}
              uniqueFiles={uniqueFiles}
            />
          </div>
          <div className="w-1/5"></div> {/* Spacer for right sidebar */}
        </div>
        <div className="border-t border-gray-100">
          <div className="px-4 lg:px-6">
            <ArticleEditorToolbar
              selectedBlockType={selectedBlockType}
              setCurrentBlockStyle={setCurrentBlockStyle}
              runEditorCommand={runEditorCommand}
              convertCurrentBlock={convertCurrentBlock}
            />
          </div>
        </div>
      </div>

      <div>
        <div className="flex gap-40 bg-neutral-100">
          <div className="flex-1 lg:pl-64 pr-0 bg-neutral-100 pb-24">
            {saveError && <div className="text-red-500 mb-4">{saveError}</div>}
            <div className="flex flex-col space-y-8 pb-6 pt-10">

              {/* Overview Section */}
              <section id="overview" className="scroll-mt-20">
                {/* Thumbnail Display or Upload - Above Editor */}
                <ArticleThumbnailPanel
                  title={title}
                  thumbnailData={thumbnailData}
                  thumbnailArray={thumbnailArray}
                  currentThumbnailIndex={currentThumbnailIndex}
                  isDraggingThumbnail={isDraggingThumbnail}
                  isUploadingThumbnail={isUploadingThumbnail}
                  isThumbnailGenerating={isThumbnailGenerating}
                  setShowGalleryModal={setShowGalleryModal}
                  handleThumbnailPrevious={handleThumbnailPrevious}
                  handleThumbnailNext={handleThumbnailNext}
                  handleThumbnailSelect={handleThumbnailSelect}
                  handleSetAsThumbnail={handleSetAsThumbnail}
                  handleThumbnailRegenerate={handleThumbnailRegenerate}
                  handleThumbnailDownload={handleThumbnailDownload}
                  handleThumbnailDelete={handleThumbnailDelete}
                  handleThumbnailDrop={handleThumbnailDrop}
                  handleThumbnailDragOver={handleThumbnailDragOver}
                  handleThumbnailDragLeave={handleThumbnailDragLeave}
                  handleThumbnailFileSelect={handleThumbnailFileSelect}
                />

                <div className="final-article">
                  <Editor
                    initialData={articleContent}
                    onChange={handleEditorChange}
                    editorRef={editorRef}
                    title={title}
                  />
                </div>
              </section>

              {/* Metadata Section */}
              <section id="metadata" className="scroll-mt-20">
                <div className="bg-white rounded-lg shadow-sm border p-6">
                  <h2 className="text-2xl font-medium mb-6 text-gray-800">{t('articles.create.seo.title')}</h2>
                  <MetaDataTab
                    metaTitle={metaTitle}
                    setMetaTitle={setMetaTitle}
                    metaDescription={metaDescription}
                    setMetaDescription={setMetaDescription}
                    handleSave={handleSave}
                    autosaveTimeoutRef={autosaveTimeoutRef}
                  />
                </div>
              </section>

              {/* FAQ Section */}
              <section id="faq" className="scroll-mt-20">
                <div className="bg-white rounded-lg shadow-sm border p-6">
                  <h2 className="text-2xl font-medium mb-6 text-gray-800">
                    Frequently Asked Questions{faqItems && faqItems.length > 0 ? ` (${faqItems.length})` : ''}
                  </h2>
                  <FAQTab
                    faqItems={faqItems}
                    setFaqItems={setFaqItems}
                    generateFAQ={generateFAQ}
                    handleSave={handleSave}
                  />
                </div>
              </section>

              {/* Bibliography Section – tylko gdy są references */}
              {hasBibliography && (
              <ArticleBibliography
                citedKnowledgeBaseSources={citedKnowledgeBaseSources}
                availableKnowledgeBaseSources={availableKnowledgeBaseSources}
                externalToolEvidence={externalToolEvidence}
                totalReferences={totalReferences}
                uniqueFiles={uniqueFiles}
                linkedSources={linkedSources}
                contentType={contentType}
                extractedReferences={extractedReferences}
                getUniqueFilenames={getUniqueFilenames}
                handleReferenceClick={handleReferenceClick}
              />
              )}

            </div>
          </div>

          {/* Right Sidebar - Always visible */}
          <div className="w-1/5 border-l border-neutral-200 bg-white px-0">
            {status === 'draft' && (
              <div className="bg-neutral-50 p-6 mb-6">
                <button
                  onClick={handleGenerateArticle}
                  disabled={true}
                  className="btn btn-muted btn-lg w-full"
                  title={t('articles.editor.generateSoon')}
                >
                  {t('articles.editor.generate')}
                </button>
              </div>
            )}
            <ArticleDetailsTabs
              articleDetails={{
                status,
                slug,
              }}
              qualityReport={qualityReport}
              onCheckQuality={handleCheckContentQuality}
              isCheckingQuality={isCheckingQuality}
            />
          </div>
        </div>
      </div>
      {tooltipContent && (
        <div
          ref={tooltipRef}
          className="fixed bg-gray-800 text-white p-2 rounded text-sm z-50 max-w-xs"
          style={{
            left: `${tooltipPosition.x}px`,
            top: `${tooltipPosition.y}px`,
          }}
        >
          {tooltipContent}
        </div>
      )}
      <SourceDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        source={selectedSource}
        workspaceId={workspaceId}
      />

      <ReferenceDrawer
        isOpen={referenceDrawerOpen}
        onClose={closeReferenceDrawer}
        reference={selectedReference}
        workspaceId={workspaceId}
      />

      {/* Thumbnail Modals */}
      <ThumbnailGalleryModal
        isOpen={showGalleryModal}
        onClose={() => setShowGalleryModal(false)}
        thumbnails={thumbnailArray}
        currentIndex={currentThumbnailIndex}
        onSelectThumbnail={(index) => {
          handleThumbnailSelect(index);
          setShowGalleryModal(false);
        }}
        onSetAsMain={(index) => {
          setCurrentThumbnailIndex(index);
          setThumbnailToSetMain(thumbnailArray[index]);
          setShowSetMainModal(true);
        }}
        onDelete={(index) => {
          setCurrentThumbnailIndex(index);
          setThumbnailToDelete(thumbnailArray[index]);
          setShowDeleteModal(true);
        }}
      />

      <ThumbnailDeleteModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setThumbnailToDelete(null);
        }}
        onConfirm={confirmDeleteThumbnail}
        thumbnail={thumbnailToDelete}
        isDeleting={isProcessingThumbnail}
      />

      <ThumbnailSetMainModal
        isOpen={showSetMainModal}
        onClose={() => {
          setShowSetMainModal(false);
          setThumbnailToSetMain(null);
        }}
        onConfirm={confirmSetAsThumbnail}
        thumbnail={thumbnailToSetMain}
        isSetting={isProcessingThumbnail}
      />

      {/* Delete Article Modal */}
      {showDeleteArticleModal && (
        <DeleteArticleModal
          title={title}
          isDeletingArticle={isDeletingArticle}
          setShowDeleteArticleModal={setShowDeleteArticleModal}
          confirmDeleteArticle={confirmDeleteArticle}
        />
      )}

      {/* Publish WordPress Confirmation Modal */}
      <PublishWordPressConfirmationModal
        open={showPublishWordPressModal}
        setOpen={setShowPublishWordPressModal}
        onConfirm={confirmPublishWebsite}
        isPublishing={isPublishing}
        articleTitle={title}
      />

      {/* Archive Confirmation Modal */}
      <ArchiveConfirmationModal
        open={showArchiveModal}
        setOpen={setShowArchiveModal}
        onConfirm={confirmArchiveArticle}
        isArchiving={isArchiving}
        articleTitle={title}
      />
    </div>
  );
};

export default ArticleDraft;
